/**
 * The one place the browser learns which language to speak.
 *
 * Language lives in React state (so switching is instant, with no reload) and
 * in localStorage (so it survives a refresh). It is deliberately *not* part of
 * any game state: the provider sits above the table, and every rendering
 * helper it hands out is a pure function of (locale, key).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  claimLabel,
  errorMessage,
  formatMessage,
  glossary,
  isLocale,
  lessonCopy,
  lessonTitle,
  meldName,
  phaseLabel,
  seatLabel,
  tileList,
  tileName,
  translate,
} from "@kikik/i18n";
import type {
  ClaimLabelKind,
  FormatOptions,
  GlossaryEntry,
  LessonCopy,
  Locale,
  Message,
  MessageParam,
  StringKey,
} from "@kikik/i18n";
import type { GroupKind, SeatWind, TileKind } from "@kikik/mahjong-core";

export const LOCALE_STORAGE_KEY = "kikik.locale";

export interface I18n {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Which string names the browser tab. Screens set it as they mount. */
  setDocumentTitleKey: (key: StringKey) => void;
  /** A plain UI string. The key is checked at compile time. */
  t: (key: StringKey, params?: Record<string, MessageParam>, options?: FormatOptions) => string;
  /** A message descriptor coming from the coach or the game log. */
  tm: (message: Message, options?: FormatOptions) => string;
  tile: (kind: TileKind) => string;
  tiles: (kinds: readonly TileKind[]) => string;
  meld: (kind: GroupKind) => string;
  claim: (kind: string) => string;
  seat: (wind: SeatWind) => string;
  phase: (phase: string) => string;
  error: (code: string, fallback?: string) => string;
  glossary: readonly GlossaryEntry[];
  lesson: (id: string) => LessonCopy | null;
  lessonTitle: (id: string) => string;
}

const LocaleContext = createContext<I18n | null>(null);

/** Bahasa Indonesia unless this browser has chosen otherwise before. */
export function readStoredLocale(): Locale {
  try {
    const stored = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    // Private-mode browsers: the default language still works.
    return DEFAULT_LOCALE;
  }
}

function storeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Remembering the choice is a convenience, never a requirement.
  }
}

export interface LocaleProviderProps {
  children: ReactNode;
  /** Test hook; normally the stored choice, or Bahasa Indonesia on a first visit. */
  locale?: Locale;
}

export function LocaleProvider({ children, locale }: LocaleProviderProps) {
  const [active, setActive] = useState<Locale>(() => locale ?? readStoredLocale());
  const [titleKey, setDocumentTitleKey] = useState<StringKey>("app.documentTitle");

  const setLocale = useCallback((next: Locale) => {
    setActive(next);
    storeLocale(next);
  }, []);

  // Assistive technology needs to know which language it is reading.
  useEffect(() => {
    const document = globalThis.document;
    if (!document) return;
    document.documentElement.lang = active;
    document.title = translate(active, titleKey);
  }, [active, titleKey]);

  const value = useMemo<I18n>(
    () => ({
      locale: active,
      setLocale,
      setDocumentTitleKey,
      t: (key, params, options) => translate(active, key, params, options),
      tm: (message, options) => formatMessage(active, message, options),
      tile: (kind) => tileName(active, kind),
      tiles: (kinds) => tileList(active, kinds),
      meld: (kind) => meldName(active, kind),
      claim: (kind) => claimLabel(active, kind as ClaimLabelKind),
      seat: (wind) => seatLabel(active, wind),
      phase: (phase) => phaseLabel(active, phase),
      error: (code, fallback) => errorMessage(active, code, fallback),
      glossary: glossary(active),
      lesson: (id) => lessonCopy(active, id),
      lessonTitle: (id) => lessonTitle(active, id),
    }),
    [active, setLocale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useI18n(): I18n {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useI18n must be used inside a <LocaleProvider>");
  return value;
}

/**
 * Name the browser tab after the screen you are on.
 *
 * The title lives in the provider rather than in each screen so that switching
 * language rewrites it without the screen having to re-run anything.
 */
export function useDocumentTitle(key: StringKey): void {
  const { setDocumentTitleKey } = useI18n();
  useEffect(() => {
    setDocumentTitleKey(key);
  }, [key, setDocumentTitleKey]);
}
